// ============================================================
// ReplaceDialog - who stands in for somebody on the map (v8.344.0).
//
// The deploy bar's REPLACE arms a pick, a click on a marker names the
// departed, and this asks who takes their spot: a member of any waiting
// room (who leaves it as the departed enters it), or a character from
// the library (admitted fresh; the departed goes to the room chosen
// below). The verb itself is `useWaitingRoomActions.replaceWith`; this
// only gathers the answer and shows its refusal.
//
// THE REFUSAL SITS WHERE THE BUTTON WOULD BE. A replace that cannot
// happen - the departed is answering an attack - is told in a sentence
// in place of CONFIRM, the way the cut dialog on feat/scenes does it,
// rather than in a toast that vanishes while you are still reading the
// list.
// ============================================================
import { useState } from 'react';
import type { Character } from '../engine/character/types';
import type { Combatant } from '../engine/roster/types';
import type { WaitingRoom } from '../engine/roster/waitingRoom';
import useFocusTrap from '../hooks/useFocusTrap.js';
import common from '../styles/common.module.css';
import styles from '../styles/SpawnDialog.module.css';
import clsx from '../utils/clsx';
import BattlefieldModal from './BattlefieldModal';

/** What `replaceWith` accepts - a room member, or a library character. */
type ReplaceSource = { roomId: string; memberId: string } | { character: Character; roomId?: string };

type Pick = { kind: 'member'; roomId: string; id: string } | { kind: 'library'; id: string };

interface ReplaceDialogProps {
  outgoing: Combatant;
  rooms: WaitingRoom[];
  library: Character[];
  /** Known before anyone is picked - the departed's own state forbids it. */
  refusal?: string | null;
  /** Returns the verb's refusal sentence, or null when the swap happened. */
  onConfirm: (source: ReplaceSource) => string | null;
  onCancel: () => void;
}

/** One candidate to stand in, as a pressed-or-not chip. */
function PickChip({
  testId,
  active,
  onPick,
  children,
}: {
  testId: string;
  active: boolean;
  onPick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={clsx(styles.presetBtn, active && styles.presetBtnActive)}
      data-testid={testId}
      aria-pressed={active}
      onClick={onPick}
    >
      {children}
    </button>
  );
}

/** The waiting rooms with anybody in them, one section each. */
function RoomSections({
  members,
  pick,
  setPick,
}: {
  members: WaitingRoom[];
  pick: Pick | null;
  setPick: (p: Pick) => void;
}) {
  return members.map((room) => (
    <div key={room.id} className={styles.section}>
      <div className={styles.sectionLabel}>{room.name}</div>
      <div className={styles.presetRow} style={{ flexWrap: 'wrap' }}>
        {room.entries.map((c) => (
          <PickChip
            key={c.id}
            testId={`replace-pick-${c.id}`}
            active={pick?.kind === 'member' && pick.id === c.id}
            onPick={() => setPick({ kind: 'member', roomId: room.id, id: c.id })}
          >
            {c.name}
          </PickChip>
        ))}
      </div>
    </div>
  ));
}

/** Where the departed goes when the newcomer comes from the library, not a room. */
function FallBackRoom({
  outgoing,
  rooms,
  toRoomId,
  setToRoomId,
}: {
  outgoing: Combatant;
  rooms: WaitingRoom[];
  toRoomId: string;
  setToRoomId: (id: string) => void;
}) {
  return (
    <label className={common.label} style={{ display: 'block', marginTop: 8 }}>
      {outgoing.name} falls back to
      <select
        className={common.input}
        data-testid="replace-room"
        aria-label="Room for the departed"
        value={toRoomId}
        onChange={(e) => setToRoomId(e.target.value)}
      >
        {rooms.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
        <option value="">A new room: Reserve</option>
      </select>
    </label>
  );
}

interface LibraryProps {
  outgoing: Combatant;
  rooms: WaitingRoom[];
  library: Character[];
  pick: Pick | null;
  setPick: (p: Pick) => void;
  toRoomId: string;
  setToRoomId: (id: string) => void;
}

function LibrarySection({ library, pick, setPick, ...room }: LibraryProps) {
  if (library.length === 0) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>Library</div>
      <div className={styles.presetRow} style={{ flexWrap: 'wrap' }}>
        {library.map((c) => (
          <PickChip
            key={c.id}
            testId={`replace-lib-${c.id}`}
            active={pick?.kind === 'library' && pick.id === c.id}
            onPick={() => setPick({ kind: 'library', id: c.id })}
          >
            {c.name}
          </PickChip>
        ))}
      </div>
      {pick?.kind === 'library' && <FallBackRoom {...room} />}
    </div>
  );
}

/** What `replaceWith` is handed for the pick: the room member, or the library character and where the departed goes. */
function sourceOf(pick: Pick, library: Character[], toRoomId: string): ReplaceSource {
  if (pick.kind === 'member') return { roomId: pick.roomId, memberId: pick.id };
  return { character: library.find((c) => c.id === pick.id) as Character, ...(toRoomId ? { roomId: toRoomId } : {}) };
}

/** Cancel, and Replace - or the refusal in its place. */
function ReplaceActions({
  blocked,
  canConfirm,
  onCancel,
  onConfirm,
}: {
  blocked: string | null;
  canConfirm: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className={styles.presetRow} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
      <button type="button" className={common.btnSmall} data-testid="replace-cancel" onClick={onCancel}>
        Cancel
      </button>
      {blocked ? (
        <span className={styles.customLabel} role="status" data-testid="replace-refusal">
          {blocked}
        </span>
      ) : (
        <button
          type="button"
          className={common.btnPrimary}
          data-testid="replace-confirm"
          disabled={!canConfirm}
          onClick={onConfirm}
        >
          Replace
        </button>
      )}
    </div>
  );
}

export default function ReplaceDialog({
  outgoing,
  rooms,
  library,
  refusal = null,
  onConfirm,
  onCancel,
}: ReplaceDialogProps) {
  const [pick, setPick] = useState<Pick | null>(null);
  // Where the departed goes when the newcomer is not from a room. The
  // hook makes a "Reserve" when there is none, so '' is a valid answer.
  const [toRoomId, setToRoomId] = useState<string>(rooms[0]?.id ?? '');
  const [lateRefusal, setLateRefusal] = useState<string | null>(null);
  const trapRef = useFocusTrap(true);
  const members = rooms.filter((r) => r.entries.length > 0);
  const confirm = () => {
    if (pick) setLateRefusal(onConfirm(sourceOf(pick, library, toRoomId)));
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };
  return (
    <BattlefieldModal
      onBackdropClick={onCancel}
      onKeyDown={onKeyDown}
      trapRef={trapRef}
      className={clsx(common.modalContent, styles.content)}
      label="Replace dialog"
      testId="replace-dialog"
    >
      <h3 className={styles.title}>Replace {outgoing.name}</h3>
      <div className={styles.subtitle}>
        Who stands on their spot? {outgoing.name} falls back to a waiting room with wounds and conditions intact.
      </div>
      <RoomSections members={members} pick={pick} setPick={setPick} />
      <LibrarySection {...{ outgoing, rooms, library, pick, setPick, toRoomId, setToRoomId }} />
      {members.length === 0 && library.length === 0 && (
        <div className={styles.subtitle}>Nobody is waiting, and the library is empty.</div>
      )}
      <ReplaceActions blocked={refusal ?? lateRefusal} canConfirm={!!pick} onCancel={onCancel} onConfirm={confirm} />
    </BattlefieldModal>
  );
}
